import React, { useEffect, useRef, useCallback } from 'react';
import { Box, useTheme } from '@mui/material';
import { brand } from '../../config/brand';

// ============================================================================
// CONFIGURATION
// ============================================================================

interface LayerConfig {
  name: string;
  shadeCount: number;
  hues: number[];
  saturation: { min: number; max: number };
  lightness: { min: number; max: number };
  alpha: { min: number; max: number };
  radius: { min: number; max: number };
  speedMultiplier: number;
  mouseRepelStrength: number;
}

interface DynamicBackgroundConfig {
  canvas: {
    backgroundColor: string;
    trailOpacity: number;
    blurAmount: string;
  };
  mouse: {
    defaultPosition: { x: number; y: number };
    influenceRadius: number;
  };
  physics: {
    velocityDamping: number;
    boundaryPushStrength: number;
    boundaryMarginRatio: number;
    collisionOverlapRatio: number;
    collisionForce: number;
  };
  animation: {
    wanderAmplitude: number;
    wanderFrequencyX: { primary: number; secondary: number };
    wanderFrequencyY: { primary: number; secondary: number };
  };
  layers: LayerConfig[];
  shade: {
    noise: {
      baseSpeed: number;
      speedVariation: number;
      offsetRange: number;
    };
    breathing: {
      baseSpeed: number;
      speedVariation: number;
      baseAmount: number;
      amountVariation: number;
    };
    colorShift: {
      baseSpeed: number;
      speedVariation: number;
      hueRange: number;
      lightnessRange: number;
      lightnessSpeedRatio: number;
    };
    gradient: {
      innerLightnessBoost: number;
      innerColorStop: number;
      midColorStop: number;
      outerColorStop: number;
      radiusMultiplier: number;
    };
    maxBaseSpeed: number;
  };
}

// Base config shared between themes
const BASE_CONFIG = {
  mouse: {
    defaultPosition: { x: -1000, y: -1000 },
    influenceRadius: 180,
  },
  physics: {
    velocityDamping: 0.96,
    boundaryPushStrength: 0.02,
    boundaryMarginRatio: 0.5,
    collisionOverlapRatio: 0.6,
    collisionForce: 0.02,
  },
  animation: {
    wanderAmplitude: 0.3,
    wanderFrequencyX: { primary: 2, secondary: 0.7 },
    wanderFrequencyY: { primary: 1.5, secondary: 0.9 },
  },
  shade: {
    noise: {
      baseSpeed: 0.001,
      speedVariation: 0.001,
      offsetRange: 1000,
    },
    breathing: {
      baseSpeed: 0.005,
      speedVariation: 0.008,
      baseAmount: 0.15,
      amountVariation: 0.15,
    },
    colorShift: {
      baseSpeed: 0.002,
      speedVariation: 0.003,
      hueRange: 15,
      lightnessRange: 5,
      lightnessSpeedRatio: 0.7,
    },
    gradient: {
      innerLightnessBoost: 5,
      innerColorStop: 0,
      midColorStop: 0.4,
      outerColorStop: 1,
      radiusMultiplier: 2,
    },
    maxBaseSpeed: 1.5,
  },
};

/**
 * Theme-specific hue overrides for DynamicBackground glow.
 * Each MUI theme gets its own glow color so themes look distinct.
 * Falls back to brand.bgHues for dark/light (default brand themes).
 */
const THEME_HUES: Record<string, number[]> = {
  dark:     brand.bgHues.dark,
  light:    brand.bgHues.light,
  midnight: [270, 275, 280, 265, 285],    // purple/violet glow (matches accent #a855f7)
  ocean:    [185, 188, 192, 195, 200],    // cyan/teal glow (matches accent #06b6d4)
};

function getHuesForTheme(themeName: string): number[] {
  return THEME_HUES[themeName] || brand.bgHues.dark;
}

// Build config dynamically per theme
function buildDarkConfig(hues: number[]): DynamicBackgroundConfig {
  return {
    ...BASE_CONFIG,
    canvas: {
      backgroundColor: 'rgba(13, 17, 23, 1)',
      trailOpacity: 0.08,
      blurAmount: '2px',
    },
    layers: [
      {
        name: 'background',
        shadeCount: 10,
        hues,
        saturation: { min: 55, max: 80 },
        lightness: { min: 25, max: 40 },
        alpha: { min: 0.18, max: 0.30 },
        radius: { min: 120, max: 200 },
        speedMultiplier: 0.7,
        mouseRepelStrength: 1.0,
      },
      {
        name: 'foreground',
        shadeCount: 12,
        hues,
        saturation: { min: 60, max: 85 },
        lightness: { min: 30, max: 45 },
        alpha: { min: 0.20, max: 0.35 },
        radius: { min: 60, max: 120 },
        speedMultiplier: 1.2,
        mouseRepelStrength: 2.0,
      },
    ],
  };
}

function buildLightConfig(hues: number[]): DynamicBackgroundConfig {
  return {
    ...BASE_CONFIG,
    canvas: {
      backgroundColor: 'rgba(246, 248, 250, 1)',
      trailOpacity: 0.06,
      blurAmount: '3px',
    },
    layers: [
      {
        name: 'background',
        shadeCount: 8,
        hues,
        saturation: { min: 20, max: 40 },
        lightness: { min: 70, max: 85 },
        alpha: { min: 0.15, max: 0.25 },
        radius: { min: 150, max: 250 },
        speedMultiplier: 0.6,
        mouseRepelStrength: 1.0,
      },
      {
        name: 'foreground',
        shadeCount: 10,
        hues,
        saturation: { min: 25, max: 45 },
        lightness: { min: 75, max: 88 },
        alpha: { min: 0.18, max: 0.30 },
        radius: { min: 80, max: 140 },
        speedMultiplier: 1.0,
        mouseRepelStrength: 2.0,
      },
    ],
  };
}

// Get config based on theme mode and theme name
const getConfigForTheme = (mode: 'light' | 'dark', themeName?: string): DynamicBackgroundConfig => {
  const hues = getHuesForTheme(themeName || mode);
  return mode === 'light' ? buildLightConfig(hues) : buildDarkConfig(hues);
};

// Legacy default config
const DEFAULT_CONFIG: DynamicBackgroundConfig = buildDarkConfig(brand.bgHues.dark);

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

const Utils = {
  randomInRange(min: number, max: number): number {
    return min + Math.random() * (max - min);
  },

  randomFromRange(range: { min: number; max: number }): number {
    return this.randomInRange(range.min, range.max);
  },

  randomFromArray<T>(array: T[]): T {
    return array[Math.floor(Math.random() * array.length)];
  },

  clampVelocity(
    vx: number,
    vy: number,
    maxMagnitude: number
  ): { vx: number; vy: number } {
    const magnitude = Math.sqrt(vx * vx + vy * vy);
    if (magnitude > maxMagnitude) {
      const scale = maxMagnitude / magnitude;
      return { vx: vx * scale, vy: vy * scale };
    }
    return { vx, vy };
  },

  hsla(hue: number, saturation: number, lightness: number, alpha: number): string {
    return `hsla(${hue}, ${saturation}%, ${lightness}%, ${alpha})`;
  },
};

// ============================================================================
// SHADE CLASS
// ============================================================================

class Shade {
  x: number;
  y: number;
  layerIndex: number;
  layerConfig: LayerConfig;
  baseRadius: number;
  radius: number;
  baseHue: number;
  hue: number;
  saturation: number;
  baseLightness: number;
  lightness: number;
  alpha: number;
  vx: number;
  vy: number;
  noiseOffsetX: number;
  noiseOffsetY: number;
  noiseSpeed: number;
  breatheOffset: number;
  breatheSpeed: number;
  breatheAmount: number;
  hueShiftOffset: number;
  hueShiftSpeed: number;

  constructor(
    x: number,
    y: number,
    radius: number,
    layerConfig: LayerConfig,
    layerIndex: number,
    config: DynamicBackgroundConfig
  ) {
    this.x = x;
    this.y = y;
    this.layerIndex = layerIndex;
    this.layerConfig = layerConfig;

    this.baseRadius = radius;
    this.radius = radius;

    this.baseHue = Utils.randomFromArray(layerConfig.hues);
    this.hue = this.baseHue;
    this.saturation = Utils.randomFromRange(layerConfig.saturation);
    this.baseLightness = Utils.randomFromRange(layerConfig.lightness);
    this.lightness = this.baseLightness;
    this.alpha = Utils.randomFromRange(layerConfig.alpha);

    this.vx = 0;
    this.vy = 0;

    const { noise, breathing, colorShift } = config.shade;
    const speedMultiplier = layerConfig.speedMultiplier;

    this.noiseOffsetX = Math.random() * noise.offsetRange;
    this.noiseOffsetY = Math.random() * noise.offsetRange;
    this.noiseSpeed = (noise.baseSpeed + Math.random() * noise.speedVariation) * speedMultiplier;

    this.breatheOffset = Math.random() * Math.PI * 2;
    this.breatheSpeed = (breathing.baseSpeed + Math.random() * breathing.speedVariation) * speedMultiplier;
    this.breatheAmount = breathing.baseAmount + Math.random() * breathing.amountVariation;

    this.hueShiftOffset = Math.random() * Math.PI * 2;
    this.hueShiftSpeed = (colorShift.baseSpeed + Math.random() * colorShift.speedVariation) * speedMultiplier;
  }

  getColor(): string {
    return Utils.hsla(this.hue, this.saturation, this.lightness, this.alpha);
  }

  draw(ctx: CanvasRenderingContext2D, config: DynamicBackgroundConfig): void {
    const { gradient: gradientConfig } = config.shade;
    const drawRadius = this.radius * gradientConfig.radiusMultiplier;

    const gradient = ctx.createRadialGradient(
      this.x, this.y, 0,
      this.x, this.y, drawRadius
    );

    const innerColor = Utils.hsla(
      this.hue,
      this.saturation,
      this.lightness + gradientConfig.innerLightnessBoost,
      this.alpha
    );
    const outerColor = Utils.hsla(this.hue, this.saturation, this.lightness, 0);

    gradient.addColorStop(gradientConfig.innerColorStop, innerColor);
    gradient.addColorStop(gradientConfig.midColorStop, this.getColor());
    gradient.addColorStop(gradientConfig.outerColorStop, outerColor);

    ctx.beginPath();
    ctx.fillStyle = gradient;
    ctx.arc(this.x, this.y, drawRadius, 0, Math.PI * 2);
    ctx.fill();
  }

  update(
    sameLayerShades: Shade[],
    mouse: { x: number; y: number; radius: number },
    bounds: { width: number; height: number },
    time: number,
    config: DynamicBackgroundConfig
  ): void {
    // Wandering
    const { animation } = config;
    this.noiseOffsetX += this.noiseSpeed;
    this.noiseOffsetY += this.noiseSpeed;

    const wanderX = Math.sin(this.noiseOffsetX * animation.wanderFrequencyX.primary)
      * Math.cos(this.noiseOffsetX * animation.wanderFrequencyX.secondary)
      * animation.wanderAmplitude;

    const wanderY = Math.sin(this.noiseOffsetY * animation.wanderFrequencyY.primary)
      * Math.cos(this.noiseOffsetY * animation.wanderFrequencyY.secondary)
      * animation.wanderAmplitude;

    this.vx += wanderX;
    this.vy += wanderY;

    // Mouse avoidance
    const dx = this.x - mouse.x;
    const dy = this.y - mouse.y;
    const distance = Math.sqrt(dx * dx + dy * dy);
    const minDistance = mouse.radius + this.radius;

    if (distance < minDistance && distance > 0) {
      const force = (minDistance - distance) / minDistance;
      const angle = Math.atan2(dy, dx);
      const strength = this.layerConfig.mouseRepelStrength;

      this.vx += Math.cos(angle) * force * strength;
      this.vy += Math.sin(angle) * force * strength;
    }

    // Collisions
    const { collisionOverlapRatio, collisionForce } = config.physics;
    for (const other of sameLayerShades) {
      if (other === this) continue;

      const cdx = other.x - this.x;
      const cdy = other.y - this.y;
      const cdist = Math.sqrt(cdx * cdx + cdy * cdy);
      const cminDist = (this.radius + other.radius) * collisionOverlapRatio;

      if (cdist < cminDist && cdist > 0) {
        const cangle = Math.atan2(cdy, cdx);
        const cforce = (cminDist - cdist) / cminDist * collisionForce;

        this.vx -= Math.cos(cangle) * cforce;
        this.vy -= Math.sin(cangle) * cforce;
        other.vx += Math.cos(cangle) * cforce;
        other.vy += Math.sin(cangle) * cforce;
      }
    }

    // Physics
    const { velocityDamping } = config.physics;
    const maxSpeed = config.shade.maxBaseSpeed * this.layerConfig.speedMultiplier;

    this.vx *= velocityDamping;
    this.vy *= velocityDamping;

    const clamped = Utils.clampVelocity(this.vx, this.vy, maxSpeed);
    this.vx = clamped.vx;
    this.vy = clamped.vy;

    this.x += this.vx;
    this.y += this.vy;

    // Boundary constraints
    const { boundaryPushStrength, boundaryMarginRatio } = config.physics;
    const margin = this.radius * boundaryMarginRatio;

    if (this.x < margin) this.vx += boundaryPushStrength;
    if (this.x > bounds.width - margin) this.vx -= boundaryPushStrength;
    if (this.y < margin) this.vy += boundaryPushStrength;
    if (this.y > bounds.height - margin) this.vy -= boundaryPushStrength;

    // Animated properties
    const { colorShift, breathing } = config.shade;

    const breathe = Math.sin(time * this.breatheSpeed + this.breatheOffset);
    this.radius = this.baseRadius * (1 + breathe * this.breatheAmount);

    const hueShift = Math.sin(time * this.hueShiftSpeed + this.hueShiftOffset);
    this.hue = this.baseHue + hueShift * colorShift.hueRange;

    const lightnessShift = Math.sin(
      time * this.breatheSpeed * colorShift.lightnessSpeedRatio + this.breatheOffset
    );
    this.lightness = this.baseLightness + lightnessShift * colorShift.lightnessRange;
  }
}

// ============================================================================
// COMPONENT PROPS
// ============================================================================

interface DynamicBackgroundProps {
  /** Custom configuration override */
  config?: Partial<DynamicBackgroundConfig>;
  /** Opacity of the background (0-1) */
  opacity?: number;
  /** Z-index for layering */
  zIndex?: number;
  /** Whether to blur the canvas */
  blur?: boolean;
}

// ============================================================================
// DYNAMIC BACKGROUND COMPONENT
// ============================================================================

const DynamicBackground: React.FC<DynamicBackgroundProps> = ({
  config: customConfig,
  opacity = 1,
  zIndex = 0,
  blur = true,
}) => {
  const theme = useTheme();
  const themeMode = theme.palette.mode;

  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number>();
  const mouseRef = useRef({ x: -1000, y: -1000, radius: 180 });
  const layersRef = useRef<Shade[][]>([]);
  const timeRef = useRef(0);
  const themeModeRef = useRef(themeMode);

  // Read the active theme name from the DOM attribute set by ThemeContext
  const themeName = typeof document !== 'undefined'
    ? document.documentElement.getAttribute('data-theme') || themeMode
    : themeMode;

  // Get theme-appropriate config and merge with custom overrides
  const baseConfig = getConfigForTheme(themeMode, themeName);
  const config: DynamicBackgroundConfig = {
    ...baseConfig,
    ...customConfig,
  };

  const initializeLayers = useCallback((width: number, height: number) => {
    layersRef.current = config.layers.map((layerConfig, layerIndex) => {
      const shades: Shade[] = [];
      for (let i = 0; i < layerConfig.shadeCount; i++) {
        const radius = Utils.randomFromRange(layerConfig.radius);
        const x = Math.random() * width;
        const y = Math.random() * height;
        shades.push(new Shade(x, y, radius, layerConfig, layerIndex, config));
      }
      return shades;
    });
  }, [config]);

  useEffect(() => {
    const container = containerRef.current;
    const canvas = canvasRef.current;
    if (!canvas || !container) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Check if theme changed and reinitialize layers
    const themeChanged = themeModeRef.current !== themeMode;
    if (themeChanged) {
      themeModeRef.current = themeMode;
      layersRef.current = []; // Clear layers to force reinitialization
    }

    const resize = () => {
      const rect = container.getBoundingClientRect();
      const width = rect.width || window.innerWidth;
      const height = rect.height || window.innerHeight;

      canvas.width = width;
      canvas.height = height;

      if (layersRef.current.length === 0) {
        initializeLayers(width, height);
      }
    };

    const handleMouseMove = (e: MouseEvent) => {
      mouseRef.current.x = e.clientX;
      mouseRef.current.y = e.clientY;
    };

    const handleMouseLeave = () => {
      mouseRef.current.x = -1000;
      mouseRef.current.y = -1000;
    };

    const handleTouchMove = (e: TouchEvent) => {
      mouseRef.current.x = e.touches[0].clientX;
      mouseRef.current.y = e.touches[0].clientY;
    };

    const handleTouchEnd = () => {
      mouseRef.current.x = -1000;
      mouseRef.current.y = -1000;
    };

    const animate = () => {
      const { backgroundColor, trailOpacity } = config.canvas;
      const match = backgroundColor.match(/\d+/g);
      if (match) {
        const [r, g, b] = match;
        ctx.fillStyle = `rgba(${r}, ${g}, ${b}, ${trailOpacity})`;
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      }

      timeRef.current++;
      const bounds = { width: canvas.width, height: canvas.height };

      for (const layer of layersRef.current) {
        for (const shade of layer) {
          shade.update(layer, mouseRef.current, bounds, timeRef.current, config);
        }
      }

      for (const layer of layersRef.current) {
        for (const shade of layer) {
          shade.draw(ctx, config);
        }
      }

      animationRef.current = requestAnimationFrame(animate);
    };

    resize();

    // Initial fill with solid background color
    const { backgroundColor } = config.canvas;
    const match = backgroundColor.match(/\d+/g);
    if (match) {
      const [r, g, b] = match;
      ctx.fillStyle = `rgba(${r}, ${g}, ${b}, 1)`;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
    }

    window.addEventListener('resize', resize);
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseleave', handleMouseLeave);
    document.addEventListener('touchmove', handleTouchMove, { passive: true });
    document.addEventListener('touchend', handleTouchEnd);

    // Start animation
    animate();

    return () => {
      window.removeEventListener('resize', resize);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseleave', handleMouseLeave);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [config, initializeLayers, themeMode, themeName]);

  return (
    <Box
      ref={containerRef}
      sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: '100%',
        height: '100%',
        zIndex,
        opacity,
        pointerEvents: 'none',
        overflow: 'hidden',
        backgroundColor: config.canvas.backgroundColor,
      }}
    >
      <canvas
        ref={canvasRef}
        style={{
          display: 'block',
          filter: blur ? `blur(${config.canvas.blurAmount})` : 'none',
        }}
      />
    </Box>
  );
};

export default DynamicBackground;
